import { FiGitBranch } from 'react-icons/fi';

export const firstConditionalData = {
  courseId: 'first-conditional',
  courseType: 'grammar',
  title: 'First Conditional',
  icon: FiGitBranch,
  overview: {
    impact: 'Discuss likely future situations with confidence',
    focus: 'If + present simple, will + base verb',
    uses: 'Speaking Part 3, Writing Task 2',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Structure',
      content: `<p><strong>If + present simple, will + base verb</strong></p><p>The 'if' clause can come first or second.</p>`,
      examples: [
        {
          label: 'Standard',
          text: 'If it rains, I will stay home.'
        },
        {
          label: 'Reversed',
          text: 'I will stay home if it rains.'
        },
        {
          label: 'Negative',
          text: 'If you don\'t study, you won\'t pass.'
        }
      ]
    },
    {
      title: 'Uses',
      content: `<p>Use first conditional for:</p>`,
      points: [
        'Real/likely future situations: \'If I get the job, I\'ll move to London.\'',
        'Warnings: \'If you eat too much, you\'ll feel sick.\'',
        'Promises: \'If you help me, I\'ll buy you lunch.\'',
        'Predictions: \'If the population grows, there will be more traffic.\''
      ]
    },
    {
      title: 'Variations',
      content: `<p>You can replace 'will' with other modals:</p>`,
      points: [
        'can: \'If you finish early, you can leave.\'',
        'may/might: \'If it\'s sunny, we might go to the beach.\'',
        'should: \'If you feel ill, you should see a doctor.\'',
        'must: \'If you want to pass, you must study.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Complete the First Conditional',
      type: 'select',
      instructions: 'Choose the correct form.',
      questions: [
        {
          text: 'If she ___ (study) hard, she will pass.',
          options: [
            'studies',
            'will study',
            'studied'
          ],
          answer: 'studies',
          explanation: 'If-clause uses present simple, not \'will\'.'
        },
        {
          text: 'I ___ (help) you if you ask me.',
          options: [
            'will help',
            'help',
            'helped'
          ],
          answer: 'will help',
          explanation: 'Main clause uses \'will\' + base verb.'
        },
        {
          text: 'If it ___ (not rain), we\'ll go for a walk.',
          options: [
            'doesn\'t rain',
            'won\'t rain',
            'didn\'t rain'
          ],
          answer: 'doesn\'t rain',
          explanation: 'Negative in if-clause: doesn\'t + base verb.'
        },
        {
          text: 'If they ___ (arrive) late, they will miss the bus.',
          options: [
            'arrive',
            'will arrive',
            'arrived'
          ],
          answer: 'arrive',
          explanation: 'If-clause = present simple.'
        }
      ]
    }
  ],
  tips: [
    'NEVER use \'will\' in the if-clause: \'If it will rain\' is WRONG',
    'The if-clause uses present simple even though meaning is future',
    'First conditional = real/likely situations',
    'Great for IELTS Speaking Part 3 predictions'
  ],
  dailyPractice: [
    'Practice first conditional concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'First Conditional: if + present simple, will + base verb. For real/likely future situations. Common in IELTS Speaking Part 3 and Writing Task 2 predictions.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...firstConditionalData,
      title: firstConditionalData.title + ' (Foundations)',
      overview: { ...firstConditionalData.overview, duration: '15-20 minutes' },
      sections: firstConditionalData.sections.slice(0, Math.max(2, Math.ceil(firstConditionalData.sections.length / 2))),
      exercises: firstConditionalData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...firstConditionalData,
      title: firstConditionalData.title + ' (Advanced)',
      overview: { ...firstConditionalData.overview, duration: '30-35 minutes' },
      sections: firstConditionalData.sections,
      exercises: firstConditionalData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(firstConditionalData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return firstConditionalData;
}
